import type { SeverityLevel } from '@sentry/core';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const SENTRY_ENV =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) || import.meta.env.MODE;
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;

function isAutomatedBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
}

export function initSentry() {
  if (typeof window === 'undefined') return;
  if (isAutomatedBrowser()) return;
  if (!SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.warn('Sentry DSN not set — error tracking disabled');
    }
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    release: SENTRY_RELEASE,

    tracesSampleRate: SENTRY_ENV === 'production' ? 0.2 : 1.0,

    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    tracePropagationTargets: [
      'localhost',
      '127.0.0.1',
      /^https:\/\/lexgat-backend\.fly\.dev/,
      /^\/api\//,
    ],

    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
      }),
    ],

    ignoreErrors: [
      'top.GLOBALS',
      'NetworkError when attempting to fetch resource',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'auth/popup-closed-by-user',
      'Firebase: Error (auth/popup-closed-by-user).',
    ],

    beforeSend(event) {
      if (
        typeof window !== 'undefined' &&
        window.location.hostname === 'localhost' &&
        SENTRY_ENV === 'production'
      ) {
        return null;
      }
      return event;
    },
  });
}

export function identifySentryUser(
  userId: string,
  properties?: { email?: string; name?: string },
) {
  if (typeof window === 'undefined' || !SENTRY_DSN) return;
  Sentry.setUser({
    id: userId,
    email: properties?.email,
    username: properties?.name,
  });
}

export function clearSentryUser() {
  if (typeof window === 'undefined' || !SENTRY_DSN) return;
  Sentry.setUser(null);
}

export function captureSentryException(error: unknown, context?: Record<string, unknown>) {
  if (typeof window !== 'undefined') {
    void import('./platformErrorLog')
      .then(({ logPlatformError }) => logPlatformError(error, context))
      .catch(() => {});
  }
  if (typeof window === 'undefined' || !SENTRY_DSN) return;
  Sentry.captureException(error, { extra: context });
}

export function captureSentryMessage(message: string, level: SeverityLevel = 'info') {
  if (typeof window === 'undefined' || !SENTRY_DSN) return;
  Sentry.captureMessage(message, level);
}
